import { getCsrfToken } from './auth';

// Helpers for the in-browser Sunshine stream (moonlight-web behind /sunshine/).
// The Flask routes live under /api/sunshine/*; the iframe itself loads
// /sunshine/<path> same-origin through nginx.

export async function getSunshineHosts() {
	const res = await fetch('/api/sunshine/hosts', { credentials: 'include' });
	const data = await res.json().catch(() => ({}));
	if (!res.ok) {
		throw new Error(data.error || 'Streaming gateway unreachable');
	}
	return data.hosts || [];
}

export async function getSunshineHost(ip) {
	const hosts = await getSunshineHosts();
	return hosts.find((h) => h.address === ip) || null;
}

export async function getDesktopStreamPath(host) {
	const res = await fetch(`/api/sunshine/apps?host_id=${host.host_id}`, {
		credentials: 'include',
	});
	const data = await res.json().catch(() => ({}));
	if (!res.ok) {
		throw new Error(data.error || 'Could not list apps on the devkit');
	}
	const apps = data.apps || [];
	const desktop = apps.find((a) => /desktop/i.test(a.title)) || apps[0];
	if (!desktop) {
		throw new Error('No streamable apps configured in Sunshine on this devkit');
	}
	// WebRTC media doesn't survive Docker Desktop's UDP forwarding on a dev
	// machine — it "connects" but carries no frames (black screen), so the
	// automatic WebSocket fallback never fires. Force WebSocket locally;
	// production (real host networking + nat_1to1 public IP) uses WebRTC.
	const isLocalDev = ['localhost', '127.0.0.1'].includes(
		window.location.hostname
	);
	return (
		`stream.html?hostId=${host.host_id}&appId=${desktop.app_id}` +
		(isLocalDev ? '&dataTransport=WebSocket' : '')
	);
}

export async function pairSunshineHost(ip) {
	const res = await fetch('/api/sunshine/pair', {
		method: 'POST',
		credentials: 'include',
		headers: {
			'Content-Type': 'application/json',
			'X-CSRF-TOKEN': getCsrfToken(),
		},
		body: JSON.stringify({ host: ip }),
	});
	const data = await res.json().catch(() => ({}));
	if (!res.ok) throw new Error(data.error || 'Pairing failed');
	return data;
}

export async function removeSunshineHost(hostId) {
	const res = await fetch(`/api/sunshine/host?host_id=${hostId}`, {
		method: 'DELETE',
		credentials: 'include',
		headers: { 'X-CSRF-TOKEN': getCsrfToken() },
	});
	const data = await res.json().catch(() => ({}));
	if (!res.ok) throw new Error(data.error || 'Could not remove host');
	return data;
}

// Is Sunshine installed / reachable on a devkit? Drives Install vs Reinstall.
export async function getInstallStatus(ip) {
	const res = await fetch(`/api/sunshine/install-status?host=${ip}`, {
		credentials: 'include',
	});
	const data = await res.json().catch(() => ({}));
	if (!res.ok) throw new Error(data.error || 'Could not check install status');
	return data; // { reachable, installed, platform }
}

// Install/update Sunshine on a devkit, streaming the log over a WebSocket.
// Calls onLine(text) per log line and onDone(exitStatus) at the end; returns
// a function that closes the socket early.
export function installSunshine(ip, { devkitId, script, onLine, onDone } = {}) {
	const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
	let url = `${proto}://${window.location.host}/api/sunshine-install?host=${ip}`;
	if (devkitId) url += `&devkit_id=${encodeURIComponent(devkitId)}`;
	if (script) url += `&script=${encodeURIComponent(script)}`;

	const ws = new WebSocket(url);
	ws.onmessage = (e) => {
		let msg;
		try {
			msg = JSON.parse(e.data);
		} catch {
			return;
		}
		if (msg.type === 'log') onLine?.(msg.line);
		else if (msg.type === 'done') onDone?.(msg.exit_status);
	};
	ws.onerror = () => onLine?.('*** connection error ***');
	ws.onclose = () => onLine?.('*** connection closed ***');
	return () => ws.close();
}

// Restart the Sunshine service on the devkit over SSH (admin-only backend
// route) — the remedy for stuck sessions and unexplained black screens.
// devkit_id lets the backend pick up per-device SSH credentials
// (vnc_username/vnc_password on the Back4App record — Mac Minis).
export async function restartSunshine(ip, devkitId) {
	const res = await fetch('/api/sunshine/restart', {
		method: 'POST',
		credentials: 'include',
		headers: {
			'Content-Type': 'application/json',
			'X-CSRF-TOKEN': getCsrfToken(),
		},
		body: JSON.stringify({ host: ip, devkit_id: devkitId }),
	});
	const data = await res.json().catch(() => ({}));
	if (!res.ok) throw new Error(data.error || 'Could not restart Sunshine');
	return data;
}

// End the active stream session on a devkit (moonlight-web stops the app on
// the Sunshine side). Same-origin call straight to the gateway; nginx
// authenticates it with the dashboard JWT cookie like every /sunshine/ hit.
export async function endStreamSession(hostId) {
	const res = await fetch('/sunshine/api/host/cancel', {
		method: 'POST',
		credentials: 'include',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ host_id: hostId }),
	});
	if (!res.ok) throw new Error('Could not end the session');
	return res.json().catch(() => ({}));
}

// Resolve a devkit's desktop stream, auto-pairing first for admins. Reports
// progress via onStage('checking'|'pairing'|'starting') and throws with a
// user-readable message on failure — callers decide how to present errors;
// there is no silent fallback. Returns { path, hostId }.
export async function resolveStream(ip, { admin = false, onStage } = {}) {
	onStage?.('checking');
	let host = await getSunshineHost(ip);

	if (!host || host.paired !== 'Paired') {
		if (!admin) {
			throw new Error(
				'This devkit is not paired for streaming yet — ask an admin to pair it.'
			);
		}
		onStage?.('pairing');
		await pairSunshineHost(ip);
		host = await getSunshineHost(ip);
		if (!host || host.paired !== 'Paired') {
			throw new Error('Pairing did not complete — the devkit never confirmed.');
		}
	}

	onStage?.('starting');
	const path = await getDesktopStreamPath(host);
	return { path, hostId: host.host_id };
}
